import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, X } from "lucide-react";
import { api } from "@/lib/api";
import { adminMessages, adminSendMessage } from "@/lib/applyApi";
import { fmtMT } from "@/lib/mountainTime";
import { MessageThread } from "@/components/MessageThread";

const STATUS = { sent: "Sent", delivered: "Delivered", failed: "Failed", bounced: "Bounced", queued: "Queued", skipped: "Not sent", admin_skipped: "Not sent (unticked)", pending_configuration: "Not sent (channel not set up)" };
const label = (s) => (s || "").replace(/_/g, " ").replace(/^./, (x) => x.toUpperCase());

// Recorded email/text notifications for this application (existing notification_queue). Only stored facts are shown.
function DeliveryHistory({ appId, refresh }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/applications/${appId}/notifications`).then((r) => setRows([...r.data].reverse())).catch(() => setRows([])); }, [appId, refresh]);
  if (!rows) return null;
  return (
    <section data-testid="workspace-delivery-history" className="border-t border-stone-200 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Email & text history</h3>
      {!rows.length ? <p className="text-xs text-stone-500">No emails or texts recorded for this horse.</p> : (
        <ol className="space-y-2">
          {rows.map((n, i) => (
            <li key={n.id || i} data-testid={`delivery-row-${i}`} className="text-xs rounded-lg border border-stone-200 bg-white p-2">
              <div className="flex justify-between gap-2"><span className="font-semibold text-stone-800">{label(n.event)} · {n.channel === "sms" ? "Text" : label(n.channel)}</span>
                <span className={n.status === "failed" || n.status === "bounced" ? "text-red-700 font-semibold" : "text-stone-600"}>{STATUS[n.status] || label(n.status)}</span></div>
              {n.to && <div className="text-stone-600 break-all">To: {n.to}</div>}
              <div className="text-stone-500">{fmtMT(n.sent_at || n.created_at)}{n.sent_at ? "" : n.created_at ? " (recorded)" : ""}</div>
              {n.error && <div className="text-red-700">{n.error}</div>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// Persistent Horse Workspace Messages panel: the existing per-application seller conversation (same thread as the application page).
export function WorkspaceMessages({ appId, onClose }) {
  const [k, setK] = useState(0);
  const load = useCallback((o) => adminMessages(appId, o), [appId]);
  const send = useCallback((t) => adminSendMessage(appId, t), [appId]);
  return (
    <aside data-testid="workspace-messages-panel" className="absolute lg:static inset-0 z-[60] lg:z-auto lg:w-[24rem] lg:shrink-0 bg-white lg:border-l border-stone-200 flex flex-col min-h-0">
      <div className="on-dark h-14 shrink-0 flex items-center gap-2 px-3">
        <button type="button" data-testid="workspace-messages-back" onClick={onClose} aria-label="Back to Horse Workspace" className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full text-white hover:bg-white/10"><ArrowLeft className="h-5 w-5" /></button>
        <span className="font-display font-bold uppercase tracking-[0.06em] text-white flex-1">Messages</span>
        <button type="button" data-testid="workspace-messages-close" onClick={onClose} aria-label="Close messages" className="hidden lg:inline-flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-white/10"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {!appId ? <p data-testid="workspace-messages-none" className="p-4 text-sm text-stone-600">This horse has no seller application, so there is no seller conversation.</p> : <>
          <div className="p-3">
            <p data-testid="workspace-messages-channel" className="mb-2 text-xs text-stone-600">Messages post to the seller's Seller Portal. The seller is also alerted by their saved contact preference (email or text); each alert's delivery status appears below. All times {(window.__auctionClose || "Mountain Time").replace(/^\S+ [AP]M /, "")}.</p>
            <MessageThread viewer="admin" load={load} send={send} title="Messages with Seller" onSent={() => setK((x) => x + 1)} />
          </div>
          <DeliveryHistory appId={appId} refresh={k} />
        </>}
      </div>
    </aside>
  );
}
